import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster, toast } from "react-hot-toast";
import {
  createClient,
  createClientLicense,
  createClientWallet,
  createStarterPurchaseOrder,
  createWithdrawalAccount,
  getClientProfileByEmail,
} from "../api/enrollment-api";
import { useAuth } from "../context/authContext";
import { setCookie } from "../helpers/cookies";
import { AuthBackdrop, AuthFooter, generateNetworkCode } from "./enroll";

const STARTER_PO_AMOUNT = 1000000;
const STARTER_PO_UNIT = 1000000;

const defaultForm = {
  bankName: "",
  accountName: "",
  accountNumber: "",
  ratePerUnit: "",
  minAmount: "",
  maxAmount: "",
  colorScheme: "green",
};

const colorOptions = [
  "green",
  "red",
  "blue",
  "yellow",
  "orange",
  "purple",
  "pink",
  "cyan",
  "teal",
  "gray",
];

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

const getEnrollmentNetwork = (...values) => {
  const network = values
    .map((value) => String(value || "").trim())
    .find((value) => value && value.toLowerCase() !== "unassigned");

  return network || generateNetworkCode();
};

export default function ProfileSetup({ embedded = false }) {
  const navigate = useNavigate();
  const { user, setClient, setProfileChecked, setUser } = useAuth();
  const [formData, setFormData] = useState(() => {
    const saved = readSetupData();
    return {
      ...defaultForm,
      accountName: saved.businessName || user?.name || "",
    };
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);

  const images = useMemo(
    () => [
      require("../assets/images/girl-laptop.jpg"),
      require("../assets/images/men-happy.jpg"),
      require("../assets/images/girl-microwave.jpg"),
    ],
    [],
  );

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [images.length]);

  const setupData = readSetupData();
  const businessName = setupData.businessName || user?.name || "";
  const email = setupData.email || user?.email || "";

  const updateField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const requiredFields = [
      ["bankName", "Bank name"],
      ["accountName", "Account name"],
      ["accountNumber", "Account number"],
      ["ratePerUnit", "Rate per unit"],
      ["minAmount", "Min amount"],
      ["maxAmount", "Max amount"],
      ["colorScheme", "Color scheme"],
    ];

    const missingField = requiredFields.find(
      ([key]) => !String(formData[key] || "").trim(),
    );

    if (missingField) {
      setFieldErrors((prev) => ({
        ...prev,
        [missingField[0]]: `${missingField[1]} is required`,
      }));
      return false;
    }

    const ratePerUnit = Number(formData.ratePerUnit);
    const minAmount = Number(formData.minAmount);
    const maxAmount = Number(formData.maxAmount);

    if (!Number.isFinite(ratePerUnit) || ratePerUnit <= 0) {
      setFieldErrors((prev) => ({
        ...prev,
        ratePerUnit: "Rate per unit must be greater than zero",
      }));
      return false;
    }

    if (!Number.isFinite(minAmount) || minAmount <= 0) {
      setFieldErrors((prev) => ({
        ...prev,
        minAmount: "Min amount must be greater than zero",
      }));
      return false;
    }

    if (!Number.isFinite(maxAmount) || maxAmount <= 0) {
      setFieldErrors((prev) => ({
        ...prev,
        maxAmount: "Max amount must be greater than zero",
      }));
      return false;
    }

    if (maxAmount < minAmount) {
      setFieldErrors((prev) => ({
        ...prev,
        maxAmount: "Max amount must be greater than min amount",
      }));
      return false;
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setLoading(true);

    try {
      const network = getEnrollmentNetwork(setupData.network, user?.network);
      const landlordPayload = {
        name: businessName,
        network,
        portal_url: window.location.origin,
        color_scheme: formData.colorScheme,
        reorder_limit: 0,
        rate_per_unit: Number(formData.ratePerUnit),
        payment_gateway: "PAYSTACK",
        bank_name: formData.bankName.trim(),
        account_name: formData.accountName.trim(),
        account_number: formData.accountNumber.trim(),
        is_active: true,
        email,
        cap: 0,
        percentage: 0,
        flat_fee: 200,
        min_amount: Number(formData.minAmount),
        max_amount: Number(formData.maxAmount),
        type: "provider",
      };

      // Create and reload the landlord client profile so downstream setup has the client ID.
      await createClient(landlordPayload);
      const initialClientProfile = normalizeClientProfile(
        await getClientProfileByEmail(email),
      );
      const clientId = getClientId(initialClientProfile);

      if (!clientId) {
        throw {
          message: "Client profile was created, but no client ID was returned.",
        };
      }

      const now = new Date();
      const unendingDate = new Date("9999-12-31T23:59:59.999Z");

      // Create a license for the landlord with an unending end date
      await createClientLicense({
        client_id: clientId,
        start_date: now.toISOString(),
        end_date: unendingDate.toISOString(),
        created_by: email,
      });

      // Create a wallet and withdrawal account for the landlord
      await createClientWallet({ client_id: clientId, currency: "NGN" });
      await createWithdrawalAccount({ client_id: clientId, currency: "NGN" });

      // create a starter purchase order for the landlord
      await createStarterPurchaseOrder({
        external_token: `LANDLORD-ENROLL-${Date.now()}`,
        amount_purchased: STARTER_PO_AMOUNT,
        unit_purchased: STARTER_PO_UNIT,
        network,
      });

      const clientProfile = normalizeClientProfile(
        await getClientProfileByEmail(email),
      );

      sessionStorage.removeItem("landlord_profile_setup");
      setClient(clientProfile);
      setProfileChecked(true);
      setUser((prevUser) => (prevUser ? { ...prevUser, network } : prevUser));
      setCookie("network", network);
      setCookie(
        "user",
        JSON.stringify({
          name: user?.name,
          email: user?.email,
          network,
        }),
      );
      toast.success("Profile setup completed.");
      navigate("/dashboard", { replace: true });
    } catch (error) {
      toast.error(
        getErrorMessage(error, "Profile setup failed. Please try again."),
      );
    } finally {
      setLoading(false);
    }
  };

  const form = (
    <div className={embedded ? "w-full" : "relative z-20 w-full max-w-xl"}>
      <form
        onSubmit={handleSubmit}
        className={
          embedded
            ? "w-full rounded-2xl bg-white p-5 shadow-sm md:p-7"
            : "w-full rounded-xl bg-black/55 p-6 shadow-2xl md:p-8"
        }
      >
        <div className="mb-6">
          <h1
            className={`text-2xl font-bold md:text-3xl ${
              embedded ? "text-primary-dark" : "text-white"
            }`}
          >
            Profile Setup
          </h1>
          <p
            className={`mt-2 text-sm ${
              embedded ? "text-subText" : "text-primary-light"
            }`}
          >
            Add settlement details to complete your landlord profile.
          </p>
        </div>

        <div className="grid gap-6">
          <FormGroup
            title="Settlement details"
            description="This account will receive provider withdrawals."
            embedded={embedded}
          >
            <div className="grid gap-4 md:grid-cols-3">
              <ProfileField
                label="Bank name"
                name="bankName"
                value={formData.bankName}
                onChange={updateField}
                error={fieldErrors.bankName}
                embedded={embedded}
                required
              />
              <ProfileField
                label="Account name"
                name="accountName"
                value={formData.accountName}
                onChange={updateField}
                error={fieldErrors.accountName}
                embedded={embedded}
                required
              />
              <ProfileField
                label="Account number"
                name="accountNumber"
                inputMode="numeric"
                value={formData.accountNumber}
                onChange={updateField}
                error={fieldErrors.accountNumber}
                embedded={embedded}
                required
              />
            </div>
          </FormGroup>

          <FormGroup
            title="Client configuration"
            description="These values control vending, limits, and portal appearance."
            embedded={embedded}
          >
            <div className="grid gap-4 md:grid-cols-2">
              <ProfileField
                label="Rate per unit"
                name="ratePerUnit"
                type="number"
                inputMode="decimal"
                value={formData.ratePerUnit}
                onChange={updateField}
                error={fieldErrors.ratePerUnit}
                embedded={embedded}
                required
              />
              <ProfileField
                label="Min amount"
                name="minAmount"
                type="number"
                inputMode="decimal"
                value={formData.minAmount}
                onChange={updateField}
                error={fieldErrors.minAmount}
                embedded={embedded}
                required
              />
              <ProfileField
                label="Max amount"
                name="maxAmount"
                type="number"
                inputMode="decimal"
                value={formData.maxAmount}
                onChange={updateField}
                error={fieldErrors.maxAmount}
                embedded={embedded}
                required
              />
              <label
                className={`flex flex-col gap-1 text-sm ${
                  embedded ? "text-primary-dark" : "text-white"
                }`}
              >
                <span className="font-bold">
                  Color scheme
                  <span className="ml-1 text-primary-error">*</span>
                </span>
                <select
                  name="colorScheme"
                  value={formData.colorScheme}
                  onChange={(event) =>
                    updateField("colorScheme", event.target.value)
                  }
                  className={
                    embedded
                      ? "h-11 rounded-lg border border-primary-light bg-white px-3 text-primary-dark outline-none transition focus:border-primaryDull focus:ring-2 focus:ring-primary-normal/20"
                      : "h-11 rounded border border-white/25 bg-black/30 px-3 text-white outline-none transition focus:border-primaryDull focus:ring-2 focus:ring-primary-normal/25"
                  }
                  required
                >
                  {colorOptions.map((color) => (
                    <option key={color} value={color}>
                      {color}
                    </option>
                  ))}
                </select>
                {fieldErrors.colorScheme && (
                  <span className="text-xs font-bold text-primary-error">
                    {fieldErrors.colorScheme}
                  </span>
                )}
              </label>
            </div>
          </FormGroup>
        </div>

        {loading && (
          <div className="mt-4 rounded bg-primary-light p-4">
            <div className="flex items-center gap-3 text-sm font-bold text-primary-dark">
              <Loader2 size={18} className="animate-spin" />
              Creating your account setup...
            </div>
          </div>
        )}

        <div
          className={`mt-6 flex justify-end border-t pt-5 ${
            embedded ? "border-primary-light" : "border-white/15"
          }`}
        >
          <button
            type="submit"
            disabled={loading}
            className="flex items-center justify-center gap-2 rounded-lg bg-primary-normal px-5 py-3 font-bold text-primary-dark transition hover:bg-primaryDull disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading && <Loader2 size={18} className="animate-spin" />}
            Complete setup
          </button>
        </div>
      </form>
      <Toaster position="top-center" />
    </div>
  );

  if (embedded) {
    return (
      <div className="w-full">
        {form}
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-black px-4 py-6 font-nexa">
      <AuthBackdrop images={images} currentImage={currentImage} />
      <div className="relative z-20 mb-5">
        <img
          src={require("../assets/images/logo-white.png")}
          alt="NanoWattz Logo"
          width={250}
        />
      </div>

      {form}
      <AuthFooter />
    </div>
  );
}

const readSetupData = () => {
  try {
    return JSON.parse(sessionStorage.getItem("landlord_profile_setup")) || {};
  } catch {
    return {};
  }
};

const getClientId = (profile) =>
  profile?.data?.client_id ||
  profile?.client?.client_id ||
  profile?.client_id ||
  profile?.data?.id ||
  profile?.client?.id ||
  profile?.id ||
  "";

const normalizeClientProfile = (profile) =>
  profile?.data?.data || profile?.data?.client || profile?.data || profile;

const ProfileField = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  required = false,
  inputMode,
  error,
  embedded = false,
}) => (
  <label
    className={`flex flex-col gap-1 text-sm ${
      embedded ? "text-primary-dark" : "text-white"
    }`}
  >
    <span className="font-bold">
      {label}
      {required && <span className="ml-1 text-primary-error">*</span>}
    </span>
    <input
      type={type}
      name={name}
      value={value}
      inputMode={inputMode}
      onChange={(event) => onChange(name, event.target.value)}
      className={
        embedded
          ? "h-11 rounded-lg border border-primary-light bg-white px-3 text-primary-dark outline-none transition placeholder:text-subText focus:border-primaryDull focus:ring-2 focus:ring-primary-normal/20"
          : "h-11 rounded border border-white/25 bg-transparent px-3 text-white outline-none transition placeholder:text-white/45 focus:border-primaryDull focus:ring-2 focus:ring-primary-normal/25"
      }
      required={required}
    />
    {error && (
      <span className="text-xs font-bold text-primary-error">{error}</span>
    )}
  </label>
);

const FormGroup = ({ title, description, children, embedded = false }) => (
  <section
    className={
      embedded
        ? "rounded-xl border border-primary-light bg-lightBackground p-4"
        : "rounded border border-white/15 bg-white/5 p-4"
    }
  >
    <div className="mb-4">
      <h2
        className={`text-lg font-bold ${
          embedded ? "text-primary-dark" : "text-white"
        }`}
      >
        {title}
      </h2>
      <p
        className={`mt-1 text-sm ${
          embedded ? "text-subText" : "text-primary-light"
        }`}
      >
        {description}
      </p>
    </div>
    {children}
  </section>
);
